import {
  isArray,
  isBoolean,
  isDate,
  isEmpty,
  isFunction,
  isNull,
  isObject,
  isString,
  isUndefined,
} from './common';

import { isBlob, isFile } from './validationUtils';

interface SerializeConfig {
  indices?: boolean;
  nullsAsUndefineds?: boolean;
  booleansAsIntegers?: boolean;
  allowEmptyArrays?: boolean;
  adapters?: Record<string, (value: unknown) => unknown>;
}

function inspectFormData(formData: FormData): void {
  for (const pair of formData.entries()) {
    console.debug(`${pair[0]}, ${pair[1]}`);
  }
}

function dummyFormSubmit(
  name: string,
  values: unknown,
  delay = 2000,
): Promise<void> {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      console.log(`---- | ${name} | ----`);
      console.table(values);
      resolve();
    }, delay);
  });
}

function serialize(
  obj: unknown,
  config: SerializeConfig = {},
  formData: FormData | null = null,
  key = '',
): FormData {
  // Review config section
  config = config || {};
  config.indices = isUndefined(config.indices) ? false : config.indices;
  config.nullsAsUndefineds = isUndefined(config.nullsAsUndefineds)
    ? false
    : config.nullsAsUndefineds;
  config.booleansAsIntegers = isUndefined(config.booleansAsIntegers)
    ? false
    : config.booleansAsIntegers;
  config.allowEmptyArrays = isUndefined(config.allowEmptyArrays)
    ? false
    : config.allowEmptyArrays;
  config.adapters = isEmpty(config.adapters) ? {} : config.adapters;

  const fd = formData || new FormData();

  // Undefined case
  if (isUndefined(obj)) {
    return fd;
  }

  // Null case
  if (isNull(obj)) {
    if (!config.nullsAsUndefineds) {
      fd.append(key, '');
    }
    // Boolan case
  } else if (isBoolean(obj)) {
    if (config.booleansAsIntegers) {
      fd.append(key, obj ? '1' : '0');
    } else {
      fd.append(key, String(obj));
    }
    // Array case
  } else if (isArray(obj)) {
    if (obj.length) {
      obj.forEach((value, index) => {
        const _key = isString(value) ? key : `${key}[${index}]`;
        serialize(value, config, fd, _key);
      });
    } else if (config.allowEmptyArrays) {
      fd.append(key, '');
    }
    // Date case
  } else if (isDate(obj)) {
    fd.append(key, obj.toISOString());
    // Object/ !File/!Blob case.
  } else if (isObject(obj) && !isFile(obj) && !isBlob(obj)) {
    Object.keys(obj).forEach((prop) => {
      const value = obj[prop];

      if (
        config.adapters &&
        prop in config.adapters &&
        isFunction(config.adapters[prop])
      ) {
        fd.append(prop, String(config.adapters[prop]!(value)));
      } else {
        // Process nested array
        let resolvedProp = prop;
        if (isArray(value)) {
          while (
            resolvedProp.length > 2 &&
            resolvedProp.lastIndexOf('[]') === resolvedProp.length - 2
          ) {
            resolvedProp = `[${resolvedProp.substring(0, resolvedProp.length - 2)}]`;
          }
        }

        const _key = key
          ? key[key.length - 1] === ']'
            ? `${key}${resolvedProp}`
            : `${key}.${resolvedProp}`
          : resolvedProp;

        // Process object values
        serialize(value, config, fd, _key);
      }
    });
    // Other cases
  } else {
    fd.append(key, obj as string | Blob);
  }

  return fd;
}

export { dummyFormSubmit, inspectFormData, serialize };
